import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import { InstallPrompt } from "@/components/InstallPrompt";
import { OfflineSync } from "@/components/OfflineSync";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AppLayout from "@/pages/AppLayout";
import Dashboard from "@/pages/Dashboard";
import Machines from "@/pages/Machines";
import MachineDetail from "@/pages/MachineDetail";
import MachineForm from "@/pages/MachineForm";
import Reminders from "@/pages/Reminders";
import LogService from "@/pages/LogService";
import Team from "@/pages/Team";
import Billing from "@/pages/Billing";
import Referrals from "@/pages/Referrals";
import Settings from "@/pages/Settings";
import Notifications from "@/pages/Notifications";
import Admin from "@/pages/Admin";
import AdminFleetDetail from "@/pages/AdminFleetDetail";
import AdminUserProfile from "@/pages/AdminUserProfile";
import AdminMachineDetail from "@/pages/AdminMachineDetail";
import AdminCRM from "@/pages/AdminCRM";
import AdminLeadDetail from "@/pages/AdminLeadDetail";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import PublicMachine from "@/pages/PublicMachine";
import WorkOrders from "@/pages/WorkOrders";
import Analytics from "@/pages/Analytics";
import Compliance from "@/pages/Compliance";
import PreStart from "@/pages/PreStart";
import MemberProfile from "@/pages/MemberProfile";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen bg-[#0D0E11] flex items-center justify-center font-head text-2xl text-[#FFB800] tracking-widest uppercase">Loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/m/:id" element={<PublicMachine />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/register" element={<Register />} />
          <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
          <Route element={<Protected><AppLayout /></Protected>}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/machines" element={<Machines />} />
            <Route path="/machines/new" element={<MachineForm />} />
            <Route path="/machines/:id" element={<MachineDetail />} />
            <Route path="/machines/:id/edit" element={<MachineForm />} />
            <Route path="/machines/:id/log" element={<LogService />} />
            <Route path="/reminders" element={<Reminders />} />
            <Route path="/work-orders" element={<WorkOrders />} />
            <Route path="/analytics" element={<Analytics />} />
            <Route path="/compliance" element={<Compliance />} />
            <Route path="/prestart" element={<PreStart />} />
            <Route path="/team" element={<Team />} />
            <Route path="/team/:id" element={<MemberProfile />} />
            <Route path="/billing" element={<Billing />} />
            <Route path="/referrals" element={<Referrals />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/admin/fleets/:id" element={<AdminFleetDetail />} />
            <Route path="/admin/users/:id" element={<AdminUserProfile />} />
            <Route path="/admin/machines/:id" element={<AdminMachineDetail />} />
            <Route path="/admin/crm" element={<AdminCRM />} />
            <Route path="/admin/crm/:id" element={<AdminLeadDetail />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </ErrorBoundary>
      <Toaster position="top-right" theme="dark" />
      <InstallPrompt />
      <OfflineSync />
    </AuthProvider>
  );
}

export default App;
